import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiUsers, 
  FiFileText, 
  FiDollarSign,
  FiRefreshCw,
  FiClock,
  FiCheckCircle,
  FiXCircle
} from 'react-icons/fi';
import { 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import CountUp from 'react-countup';
import toast from 'react-hot-toast';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalApplications: 0,
    pendingApplications: 0,
    approvedApplications: 0,
    totalRevenue: 0
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) fetchDashboardData();
    // eslint-disable-next-line
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🔧 Fetching dashboard data for role:', user?.role);
      
      if (user?.role === 'admin') {
        // Admin: fetch all dashboard data
        const [statsRes, applicationsRes, chartRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getRecentApplications(),
          dashboardAPI.getChartData()
        ]);
        setStats(statsRes?.data || statsRes || {
          totalUsers: 0,
          totalApplications: 0,
          pendingApplications: 0,
          approvedApplications: 0,
          totalRevenue: 0
        });
        setRecentApplications(Array.isArray(applicationsRes?.data) ? applicationsRes.data : 
                            Array.isArray(applicationsRes) ? applicationsRes : []);
        setChartData(Array.isArray(chartRes?.data) ? chartRes.data : 
                     Array.isArray(chartRes) ? chartRes : []);
      } else if (user?.role === 'agent') {
        // Agent: fetch agent-specific data
        const [statsRes, applicationsRes, chartRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getRecentApplications(),
          dashboardAPI.getChartData()
        ]);
        
        console.log('🔧 Agent dashboard stats:', statsRes);
        console.log('🔧 Agent recent applications:', applicationsRes);
        
        setStats(statsRes?.data || statsRes || {
          totalUsers: 0,
          totalApplications: 0,
          pendingApplications: 0,
          approvedApplications: 0,
          totalRevenue: 0
        });
        setRecentApplications(Array.isArray(applicationsRes?.data) ? applicationsRes.data : 
                            Array.isArray(applicationsRes) ? applicationsRes : []);
        setChartData(Array.isArray(chartRes?.data) ? chartRes.data : 
                     Array.isArray(chartRes) ? chartRes : []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
      setStats({});
      setRecentApplications([]);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, change }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            <CountUp end={value} duration={2} />
          </p>
          {change && (
            <p className={`text-sm mt-1 ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change > 0 ? '+' : ''}{change}% from last month
            </p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  const ApplicationStatusCard = ({ status, count, percentage, color, icon: Icon }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
    >
      <div className="flex items-center space-x-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{status}</p>
          <p className="text-lg font-bold text-gray-900">{count}</p>
          <p className="text-xs text-gray-500">{percentage}% of total</p>
        </div>
      </div>
    </motion.div>
  );

  const RecentApplicationCard = ({ application }) => (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
            <FiFileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {application.user?.name || application.applicantName || 'Unknown User'}
            </p>
            <p className="text-sm text-gray-500">
              {application.category?.name || application.category || 'Unknown Category'}
            </p>
          </div>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            application.status === 'approved' ? 'bg-green-100 text-green-800' :
            application.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {application.status}
          </span>
          <p className="text-xs text-gray-500 mt-1">
            {new Date(application.createdAt).toLocaleDateString()}
          </p>
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            {user?.role === 'admin' 
              ? 'Welcome to Chitti Finserv Admin Panel' 
              : 'Welcome to your Agent Dashboard'
            }
          </p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiRefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={user?.role === 'admin' ? "Total Users" : "My Users"}
          value={stats.totalUsers}
          icon={FiUsers}
          color="bg-blue-500"
          change={12}
        />
        <StatCard
          title={user?.role === 'admin' ? "Total Applications" : "My Applications"}
          value={stats.totalApplications}
          icon={FiFileText}
          color="bg-green-500"
          change={8}
        />
        <StatCard
          title="Pending Applications"
          value={stats.pendingApplications}
          icon={FiClock}
          color="bg-yellow-500"
          change={-5}
        />
        <StatCard
          title="Approved Applications"
          value={stats.approvedApplications}
          icon={FiCheckCircle}
          color="bg-green-600"
          change={15}
        />
      </div>

      {/* Application Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ApplicationStatusCard
          status="Pending"
          count={stats.pendingApplications || 0}
          percentage={stats.totalApplications > 0 ? Math.round((stats.pendingApplications / stats.totalApplications) * 100) : 0}
          color="bg-yellow-500"
          icon={FiClock}
        />
        <ApplicationStatusCard
          status="Approved"
          count={stats.approvedApplications || 0}
          percentage={stats.totalApplications > 0 ? Math.round((stats.approvedApplications / stats.totalApplications) * 100) : 0}
          color="bg-green-500"
          icon={FiCheckCircle}
        />
        <ApplicationStatusCard
          status="Rejected"
          count={Math.max(0, (stats.totalApplications || 0) - (stats.approvedApplications || 0) - (stats.pendingApplications || 0))}
          percentage={stats.totalApplications > 0 ? Math.round(((stats.totalApplications - (stats.approvedApplications || 0) - (stats.pendingApplications || 0)) / stats.totalApplications) * 100) : 0}
          color="bg-red-500"
          icon={FiXCircle}
        />
      </div>

      {/* Charts and Recent Applications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Applications Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {user?.role === 'admin' ? 'Applications Trend' : 'My Applications Trend'}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={Array.isArray(chartData) ? chartData : []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="applications" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Chart */}
        {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={Array.isArray(chartData) ? chartData : []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="revenue" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div> */}
        
      {/* Recent Applications */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            {user?.role === 'admin' ? 'Recent Applications' : 'My Recent Applications'}
          </h3>
          <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
            View All
          </button>
        </div>
        <div className="space-y-4">
          {Array.isArray(recentApplications) && recentApplications.length > 0 ? (
            recentApplications.map((application) => (
              <RecentApplicationCard key={application._id} application={application} />
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>{user?.role === 'admin' ? 'No recent applications found' : 'No applications created yet'}</p>
            </div>
          )}
        </div>
      </div>
      </div>

    </div>
  );
};

export default Dashboard; 